/* eslint-disable no-param-reassign */
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { merge } from 'lodash';
import { getAgentTeamMembershipInfo } from '$/domains/teamAgent/teamAgentService';
import type { AgentTeamMembershipInfoResult } from '$/domains/teamAgent/type';
import { createDefaultDeviceInfo } from '@meitu/catalyze';
import { getIsOverseaByCountryCode } from '$/common/utils/countryCodeUtil';
import { getSubscribePopup } from '$/common/utils/subscribeUtil';
import { getGclid, getGoogleClientId } from '$/utils/index';
import { setCloudWatermarkBase64 } from '$/utils/imageWatermark';
import { addBroadcastListener } from '$/common/utils/broadcastChannel';
// 静态导入 API 模块（request.ts 仅在函数体内访问 useOfficialStore，循环引用不会在模块求值阶段触发问题）
import { createRoboneoApi } from '$/api/request';
import { agentTeamCreateApi } from '$/api/agentTeamRequest';
// Canvas 相关模块（roboneo-chat 打包时 rollup 会 external 并替换为空实现 stub）
import { createCanvasApi } from '$/api/canvasRquest';
import { setShareCoverUrl, setShareFlowUrl, setShareTeamUrl } from '$/store/useShareStore';
import { setTheme } from '../useThemeStore';

import type {
  OfficialStoreType, User, InitComponentParams,
} from './types';
// import type { WithImmer } from '../immer.d';
import {
  initLanguage,
} from '../languageStore';

/**
 * 判断会员信息接口返回的余额是否允许写入顶部展示；团队态必须拿到团队会员响应才覆盖。
 */
function shouldSyncSubscribeRadishAmount(
  parameter: AgentTeamMembershipInfoResult,
  clientOId?: number,
) {
  if (!clientOId) {
    return true;
  }
  return parameter.membership_type === 'team';
}

const officialStoreSlice: (set: any, get: any) => OfficialStoreType = (set: any, get: any) => ({
  currentLocale: '',

  clientId: '',

  clientOId: undefined,

  clientOGroupId: undefined,

  clientOTeamName: undefined,

  clientOIsAdmin: undefined,

  appId: '',

  firstUrl: '',

  channel: '',

  gid: '',

  isInitComponent: false,

  env: 'development',

  languageCode: 'zh-Hans',

  isOverseaServer: undefined,
  appScene: 'roboneo',

  token: '',

  google_target_id: '',

  radishAmount: 0,

  userInfo: {
    uid: '',
    token: '',
    screenName: '',
    avatar: '',
    isChinaPhone: false,
  },

  device: createDefaultDeviceInfo(),

  currentCountryCode: 'CN',

  isOversea: () => {
    if (typeof get().isOverseaServer === 'boolean') {
      return get().isOverseaServer;
    }
    return getIsOverseaByCountryCode(get().currentCountryCode);
    // TODO 默认海外，方便测试
    // return true;
  },

  currentTheme: '',

  showHistoryModal: false,

  isUserLoggedIn: () => {
    return Boolean(get().userInfo.uid);
  },

  chatOptions: {},
  navigateMap: {
    historyStudio: (query) => {
    },
    agentCreateStudio: (query) => {
    },
    historyFlow: (query) => {
    },
    historyPPT: (query) => {
    },
    inputChatNewStudio: (query) => {
    },
    closePPTBackStudio: (query) => {
    },
    agentLogoHome: (query) => {
    },
    onCheckRoomError: (query) => {
    },
    historyCurrentRoomDelete: (query) => {
    },
    history404: (query) => {
    },
    historyCommunity: (query) => {
    },
    historyTeamFlow: (query) => {
    },
    caseDetail: (query) => {
    },
  },
  toLogin: async () => {},
  /**
   * 从 Team 域名会员信息接口刷新订阅信息，并写回全局 store。
   */
  async loadSubscribeInfo() {
    const parameter = await getAgentTeamMembershipInfo();
    set({
      userInfo: {
        ...get().userInfo,
        subscribe: {
          validTime: parameter.valid_time,
          isValid: Boolean(parameter?.is_valid_user),
          subPeriod: parameter.sub_period,
          subType: parameter.sub_type,
          invalidTime: parameter.invalid_time,
          radishDetailList: parameter.detail_list,
          level: parameter.level,
          nextLevel: parameter.next_level,
          isHighestLevel: Number(parameter.level) === Number(parameter.next_level),
          hasRecurringMonth: !!parameter?.renew_info?.commodity_id, // 是否含有连续包月
          renewInfo: {
            ...parameter.renew_info,
            sub_period: Number(parameter.renew_info?.sub_period) || 0,
          },
          isInviteCodeVisible: parameter.invite_code_entry_visible, // 邀请码是否可见
          membershipInfos: parameter.membership_infos,
          contractList: parameter.contract_list,
          showCommodityId: parameter.show_commodity_id,
          usedStorage: parameter.used_storage,
          storageLimit: parameter.storage_limit,
        },
      },
    });
    // 获取剩余萝卜；团队上下文下个人会员响应不能覆盖团队详情中的积分池余额。
    if (shouldSyncSubscribeRadishAmount(parameter, get().clientOId)) {
      setRadishAmount(Number(parameter.total_amount) || 0);
    }
  },

  /**
   * 打开旧版订阅弹窗，团队上下文下隐藏仅个人会员支持的兑换入口。
   */
  async openSubscribePopup() {
    const accessToken = get().userInfo.token;
    if (!get().isUserLoggedIn || !accessToken) {
      get().userInfo.subscribe = undefined;
      // customMessage.info('登录态已失效，请重新登录');
      await get().toLogin();
      return;
    }
    const self = get();
    await self.loadSubscribeInfo();

    const { subscribeConfig, openSubscribePopup } = await getSubscribePopup();
    const gg_client_id = await getGoogleClientId();
    const gclid = await getGclid();
    const config = merge({}, subscribeConfig, {
      clientInfo: {
        transferData: {
          bigData: {
            gg_client_id,
            gclid,
            project_name: 'roboneo',
          },
        },
      },
    });

    const modal = openSubscribePopup({
      showInvoice: false,
      showRedeemCode: !(self.clientOId && self.clientOGroupId),
      config,
      onTradeFinish() {
        self.loadSubscribeInfo();
      },
      onTradeConfirm() {
        // 支付完成关闭订购弹窗
        modal.close();

        setTimeout(() => {
          self.loadSubscribeInfo();
        }, 5000);
      },
      // onClose() { },
      onLogin() { },
    });
  },
  // 我的节点总数
  myNodesTotalNum: null,

  //
  apiConfig: {},
  sentryConfig: undefined,
} as OfficialStoreType);
export const useOfficialStore = create<OfficialStoreType>()(immer(officialStoreSlice));

export function setShowHistoryModal(showHistoryModal: boolean) {
  useOfficialStore.setState(() => ({
    showHistoryModal,
  }));
}
/**
 * 合并写入用户信息；账号 uid 变化时清空订阅快照，避免下一账号误用上一账号会员态。
 */
export function setUserInfo(userInfo: User) {
  useOfficialStore.setState((state) => {
    const uidChanged = userInfo.uid !== undefined && userInfo.uid !== state.userInfo.uid;

    return {
      userInfo: {
        ...state.userInfo, // 先保留之前的
        ...userInfo,
        ...(uidChanged ? { subscribe: undefined } : {}),
      },
    };
  });
}

/**
 * 更新团队账户 OID，同步写入请求头 Client-OID。
 */
export function setClientOId(clientOId?: number) {
  useOfficialStore.setState(() => ({
    clientOId,
  }));
}

/**
 * 更新团队账户 OGroup ID，同步写入请求头 Client-OGroupId。
 */
export function setClientOGroupId(clientOGroupId?: string) {
  useOfficialStore.setState(() => ({
    clientOGroupId,
  }));
}

/**
 * 更新团队账户名称，供 SDK 内部确认单等展示团队上下文。
 */
export function setClientOTeamName(clientOTeamName?: string) {
  useOfficialStore.setState(() => ({
    clientOTeamName,
  }));
}

/**
 * 更新当前团队账户是否为管理员，供 SDK 内部商业化埋点使用。
 */
export function setClientOIsAdmin(clientOIsAdmin?: boolean) {
  useOfficialStore.setState(() => ({
    clientOIsAdmin,
  }));
}
/** *
 * 更新萝卜
 */
export function setRadishAmount(amount: number) {
  useOfficialStore.setState(() => ({
    radishAmount: amount,
  }));
}

function setGoogleTargetId(id?: string) {
  if (id) {
    useOfficialStore.setState(() => ({
      google_target_id: id,
    }));
  }
}

/**
 * 初始化组件配置，包括语言、主题、API 等
 */
export function InitComponent(data: InitComponentParams) {
  const { loadSubscribeInfo } = useOfficialStore.getState();
  setCloudWatermarkBase64(data.watermarkBase64);
  useOfficialStore.setState(() => ({
    ...data,
    appId: data.appId || '6829803307037000000',
  }));
  initLanguage(data.languageCode || 'zh-Hans', {
    // 语言包支持 SDK 默认 CDN、宿主 CDN 和宿主自定义 loader，旧的 locales 仍作为最高优先级覆盖。
    locales: data?.locales,
    localeBaseUrl: data?.localeBaseUrl,
    loadLocale: data?.loadLocale,
    disableRemoteLocale: data?.disableRemoteLocale,
  }).catch((error) => {
    console.error('initLanguage error:', error);
  });
  setTheme(data?.theme || 'dark');

  // 初始化 API
  createRoboneoApi(data?.apiConfig);

  if (data?.apiConfig) {
    // Canvas API（roboneo-chat rollup 会 external 为空实现，调用不会报错）
    createCanvasApi(data.apiConfig);
    // Agent Team API（store 初始化后 env 已就绪，确保 baseURL 正确）
    agentTeamCreateApi(data.apiConfig.agentTeamApiConfig);
  }

  // 分享功能（roboneo-chat rollup 会 external 为空实现，调用不会报错）
  if (data.shareCoverUrl || data.shareFlowUrl || data.shareTeamUrl) {
    if (data.shareCoverUrl) setShareCoverUrl(data.shareCoverUrl);
    if (data.shareFlowUrl) setShareFlowUrl(data.shareFlowUrl);
    if (data.shareTeamUrl) setShareTeamUrl(data.shareTeamUrl);
  }

  setUserInfo(data.userInfo);
  setGoogleTargetId(data.google_target_id);
  addBroadcastListener('payComplete', () => {
    // 刷新用户信息（包含商业化信息）
    loadSubscribeInfo();
    // 赠送积分有5秒的延迟;
    setTimeout(() => {
      loadSubscribeInfo();
    }, 10000);
  });
  useOfficialStore.setState(() => ({
    isInitComponent: true,
  }));
}
export function InitLoginConfig(data: {
  toLogin: () => Promise<void>;
}) {
  useOfficialStore.setState(() => ({
    ...data,
  }));
}

export function InitNavigateMap(navigateMap: OfficialStoreType['navigateMap']) {
  useOfficialStore.setState(() => ({
    navigateMap,
  }));
}
export function setMyNodesTotalNum(myNodesTotalNum: number | null) {
  useOfficialStore.setState(() => ({
    myNodesTotalNum,
  }));
}
